import Address from './Address';
import BaseModel from './BaseModel';
import ProjectPart from './ProjectPart';
import Stock from './Stock';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import * as AT from '../../components/container/attributes';
import {HitUtils} from '@hit/components';

export default class Purchase extends BaseModel {
  constructor({
    id,
    number,
    designation,
    description = undefined,
    memo = undefined,
    supplier = undefined,
    projectPart = undefined,
    documentDate = undefined,
    amount = undefined,
    grossAmount = undefined,
    expectedResponseDate = undefined,
    expectedOrderDate = undefined,
    expectedDeliveryDate = undefined,
    documentType = undefined,
    status = undefined,
    expiryDate = undefined,
    rateContract = undefined,
    stock = undefined,
    margin = undefined,
    expectedBeginDate = undefined,
  }) {
    super(id, number, designation);
    this.description = description;
    this.memo = memo;
    this.supplier = supplier ? new Address(supplier) : null;
    this.projectPart = projectPart ? new ProjectPart(projectPart) : null;
    this.documentDate = documentDate;
    this.amount = amount;
    this.grossAmount = grossAmount;
    this.expectedResponseDate = expectedResponseDate;
    this.expectedOrderDate = expectedOrderDate;
    this.expectedDeliveryDate = expectedDeliveryDate;
    this.expectedBeginDate = expectedBeginDate;
    this.documentType = documentType
      ? HitOnlineEnums.getEnumValue(
          'PURCHASE_DOCUMENT_TYPE',
          documentType,
          translate,
        )
      : null;
    this.status = status
      ? HitOnlineEnums.getEnumValue('PROCUREMENT_STATUS', status, translate)
      : null;
    this.expiryDate = expiryDate;
    this.rateContract = rateContract ? new Purchase(rateContract) : null;
    this.stock = stock ? new Stock(stock) : null;
    this.margin = margin;
  }

  static get printTemplateType() {
    return 'purchase';
  }
  get printTemplateType() {
    return Purchase.printTemplateType;
  }

  static get config() {
    return {
      addable: true,
      duplicatable: false,
      importable: false,
      browsable: true,
      deletable: true,
    };
  }

  get config() {
    return Purchase.config;
  }

  static get fkName() {
    return 'purchase_id';
  }
  static get apiRoute() {
    return 'purchase';
  }
  static get detailPageRoute() {
    return 'purchaseDetail';
  }
  static get listPageRoute() {
    return 'purchaseList';
  }
  get fkName() {
    return Purchase.fkName;
  }
  get apiRoute() {
    return Purchase.apiRoute;
  }
  get detailPageRoute() {
    return Purchase.detailPageRoute;
  }
  get listPageRoute() {
    return Purchase.listPageRoute;
  }

  get fullDesignation() {
    return HitUtils.createStandardDesignation({
      number: this.number,
      designation: this.designation,
    });
  }
  static get listDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'supplier',
      'projectPart',
      'amount',
      'grossAmount',
      'documentDate',
      'expectedDeliveryDate',
      'tags',
      'creationDate',
      'modificationDate',
      'expiryDate',
      'status',
      'expectedResponseDate',
    ]);
  }
  static get entityListDisplayAttrs() {
    return [
      'designation',
      'documentNumber',
      'documentDate',
      'journal',
      'tags',
      'status',
    ];
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'supplier',
      'projectPart',
      'amount',
      'grossAmount',
      'documentDate',
      'tags',
      'expiryDate',
      'status',
    ]);
  }

  static get panelDisplayAddressAttrs() {
    return this.getAttributes(
      ['number', 'designation', 'amount', 'status', 'projectPart', 'tags'],
      ['creationDate'],
    );
  }

  static get panelDisplayProjectPartAttrs() {
    return this.getAttributes(
      ['number', 'designation', 'supplier', 'amount', 'status', 'tags'],
      ['creationDate'],
    );
  }

  static get joinAttrsKey() {
    return [
      'number',
      'designation',
      'supplier',
      'projectPart',
      'amount',
      'grossAmount',
      'documentDate',
      'tags',
      'status',
      'documentType',
      'expiryDate',
      'status',
    ];
  }

  static get createInitColumns() {
    return {
      designation: '',
      document_date: new Date(),
      expected_response_date: new Date(),
      expected_order_date: new Date(),
      expected_delivery_date: new Date(),
      expected_begin_date: new Date(),
      document_type: 'PURCHASE_ORDER',
      expiry_date: new Date(),
      status: 'PURCHASE_ORDER_IN_PROGRESS',
    };
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'tags',
        'status',
        'expectedDeliveryDate',
        'expectedOrderDate',
        'expectedResponseDate',
      ]),
    };
  }

  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '0.75fr'}),
      this.DESIGNATION_ATTRIBUTE(),
      this.HEADER_RICH_TEXT_ATTRIBUTE(),
      this.DESCRIPTION_RICH_TEXT_ATTRIBUTE(),
      this.FOOTER_RICH_TEXT_ATTRIBUTE(),
      new AT.FkAddressAttribute({
        name: 'supplier',
        column: 'address_id',
        joinPath: ['address!fk_purchase_address_id_address'],
        // Designation-only: full Address.joinAttrsKey embed was wiping the
        // supplier from the UI after reload (value flashed then vanished).
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-app.common.supplier',
        },
        filters: {or: ['supplier.is.true', 'potential_supplier.is.true']},
      }),
      new AT.FkProjectPartAttribute({
        name: 'projectPart',
        column: 'project_part',
        joinPath: ['project_part!fk_purchase_project_part_id_project_part'],
        tableProperties: {
          header: 'hit-base.common.project-part',
        },
      }),
      new AT.FkStockAttribute({
        name: 'stock',
        column: 'stock_id',
        onlyFetchDesignation: true,
        joinPath: ['stock!stock_id'],
        tableProperties: {
          header: 'hit-base.common.stock',
        },
      }),
      new AT.DecimalAttribute({
        name: 'amount',
        column: 'total_net_amount',
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.net-amount',
          maxWidth: '0.75fr',
        },
        forcedType: 'decimal',
        decimalPrecision: 2,
      }),
      new AT.DecimalAttribute({
        name: 'grossAmount',
        column: 'total_gross_amount',
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.gross-amount',
          maxWidth: '0.75fr',
        },
        forcedType: 'decimal',
        decimalPrecision: 2,
      }),
      new AT.EnumerationAttribute({
        name: 'documentType',
        enumType: 'PURCHASE_DOCUMENT_TYPE',
        enumClass: HitOnlineEnums,
        column: 'document_type',
        tableProperties: {
          header: 'hit-app.common.document-type',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DateAttribute({
        name: 'documentDate',
        column: 'document_date',
        tableProperties: {
          header: 'hit-app.common.document-date',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DateAttribute({
        name: 'expectedResponseDate',
        column: 'expected_response_date',
        tableProperties: {
          header: 'hit-app.purchase.expected-response-date',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DateAttribute({
        name: 'expectedOrderDate',
        column: 'expected_order_date',
        tableProperties: {
          header: 'hit-app.purchase.expected-order-date',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DateAttribute({
        name: 'expectedDeliveryDate',
        column: 'expected_delivery_date',
        tableProperties: {
          header: 'hit-app.purchase.expected-delivery-date',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DateAttribute({
        name: 'expectedBeginDate',
        column: 'expected_begin_date',
        tableProperties: {
          header: 'hit-app.purchase.expected-begin-date',
          maxWidth: '0.5fr',
        },
      }),
      this.TAGS_ATTRIBUTE({moduleUsage: 'purchase', tableOrigin: 'purchase'}),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.EnumerationAttribute({
        name: 'status',
        enumType: 'PROCUREMENT_STATUS',
        enumClass: HitOnlineEnums,
        column: 'status',
        tableProperties: {
          header: 'hit-app.common.status',
          maxWidth: '1fr',
        },
      }),
      new AT.DateAttribute({
        name: 'expiryDate',
        column: 'expiry_date',
        tableProperties: {
          header: 'hit-app.purchase.expiry-date',
          hideByDefault: true,
        },
      }),
      new AT.BooleanAttribute({
        name: 'specificMargin',
        column: 'specific_margin',
        tableProperties: {
          header: 'hit-app.common.specific-margin',
        },
      }),
      new AT.DecimalAttribute({
        name: 'margin',
        column: 'margin',
        decimalPrecision: 4,
        tableProperties: {
          header: 'hit-app.common.margin',
        },
      }),
      new AT.FkPurchaseAttribute({
        name: 'rateContract',
        fkToUpdate: 'rate_contract_id',
        joinPath: ['rate_contract_id'],
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-app.common.rate-contract',
        },
      }),
    ];
  }
}
